import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { ResponsivePie } from "@nivo/pie";
import { Trophy, Users, Calendar, Filter } from "lucide-react";
import { Card } from "../../styles/GlobalStyles";

const gamificationData = require("../../data/gamification-data.json");

// Styled Components
const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.background};
  min-height: 100vh;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize["3xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const TopSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const RankingCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  min-height: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    min-height: 350px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
    min-height: 300px;
  }
`;

const RankingTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  text-align: left;
  width: 100%;
  flex-shrink: 0;
`;

const RankingSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  text-align: left;
  width: 100%;
  flex-shrink: 0;
`;

const RankingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.lg};
  width: 100%;
  flex: 1;
`;

const TopRanking = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const RankingPodium = styled.div<{ position: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  position: relative;

  ${({ position }) =>
    position === 1 &&
    `
    order: 2;
    margin-top: -20px;
  `}

  ${({ position }) =>
    position === 2 &&
    `
    order: 1;
  `}

  ${({ position }) =>
    position === 3 &&
    `
    order: 3;
  `}
`;

const TrophyIcon = styled.div<{ position: number }>`
  width: ${({ position }) => (position === 1 ? "70px" : "50px")};
  height: ${({ position }) => (position === 1 ? "70px" : "50px")};
  border-radius: 50%;
  background-color: ${({ position }) => {
    switch (position) {
      case 1:
        return "#ffd700";
      case 2:
        return "#c0c0c0";
      case 3:
        return "#cd7f32";
      default:
        return "#e5e7eb";
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: ${({ position }) => (position === 1 ? "28px" : "20px")};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: ${({ position }) => (position === 1 ? "60px" : "45px")};
    height: ${({ position }) => (position === 1 ? "60px" : "45px")};
    font-size: ${({ position }) => (position === 1 ? "24px" : "18px")};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: ${({ position }) => (position === 1 ? "50px" : "40px")};
    height: ${({ position }) => (position === 1 ? "50px" : "40px")};
    font-size: ${({ position }) => (position === 1 ? "20px" : "16px")};
  }
`;

const RankingList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  max-height: 300px;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing.xs};

  /* Estilização personalizada do scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.medium};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.colors.border.dark};
    }
  }

  /* Para Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.border.medium}
    ${({ theme }) => theme.colors.background};
`;

const RankingItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: background-color 0.2s ease;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

const Avatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PodiumAvatarWrapper = styled.div<{ position: number }>`
  width: ${({ position }) => (position === 1 ? "100px" : "70px")};
  height: ${({ position }) => (position === 1 ? "100px" : "70px")};
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ position }) => (position === 1 ? "28px" : "20px")};
`;

const PodiumAvatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PodiumName = styled.div<{ position: number }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const PodiumScore = styled.div<{ position: number }>`
  font-size: ${({ position, theme }) =>
    position === 1
      ? theme.typography.fontSize.lg
      : theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ position }) => {
    switch (position) {
      case 1:
        return "#ffd700";
      case 2:
        return "#c0c0c0";
      case 3:
        return "#cd7f32";
      default:
        return "#666";
    }
  }};
  text-align: center;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const UserDepartment = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Points = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SimpleStatsCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  flex-direction: column;
  min-height: 200px;
`;

const SimpleStatsTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  width: 100%;
  text-align: left;
  flex-shrink: 0;
`;

const SimpleStatsContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SimpleStatsValue = styled.div`
  font-size: 40px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
  text-align: center;
`;

const SimpleStatsSubtitle = styled.div<{ positive?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ positive, theme }) =>
    positive !== undefined
      ? positive
        ? theme.colors.success
        : theme.colors.error
      : theme.colors.text.secondary};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ChartCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  flex-direction: column;
  min-height: 200px;
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  width: 100%;
  text-align: left;
  flex-shrink: 0;
`;

const ChartContentContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.lg} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
    padding: ${({ theme }) => theme.spacing.md} 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.sm} 0;
  }
`;

const ChartSection = styled.div`
  width: 180px;
  height: 180px;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 160px;
    height: 160px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 140px;
    height: 140px;
  }
`;

const LegendSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  align-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 8px;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const LegendColor = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

const LegendText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const LegendPercentage = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
`;

const ChallengesSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-top: ${({ theme }) => theme.spacing.md};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: 120px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: unset;
    width: 100%;
    padding: 12px ${({ theme }) => theme.spacing.md};
    font-size: 16px; /* Evita zoom em iOS */
  }
`;

const FilterButton = styled.button<{ isActive?: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid
    ${({ theme, isActive }) =>
      isActive ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.surface};
  color: ${({ theme, isActive }) =>
    isActive ? "#fff" : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme, isActive }) =>
      isActive ? theme.colors.primary : `${theme.colors.primary}10`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    justify-content: center;
    padding: 12px ${({ theme }) => theme.spacing.md};
    font-size: 16px;
  }
`;

const ChallengesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  justify-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing.lg};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ChallengeCard = styled(Card)<{ totalCards?: number }>`
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  width: 100%;
  max-width: ${({ totalCards }) =>
    totalCards && totalCards <= 2 ? "380px" : "none"};
  height: fit-content;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const ChallengeImageContainer = styled.div`
  height: 200px;
  background-color: ${({ theme }) => theme.colors.border.light};
  position: relative;
  overflow: hidden;
`;

const ChallengeImageElement = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ChallengeImageFallback = styled.div<{ imageUrl: string }>`
  width: 100%;
  height: 100%;
  background-image: url(${({ imageUrl }) => imageUrl});
  background-color: ${({ theme }) => theme.colors.border.light};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ChallengeStatus = styled.div<{ status: string }>`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ status }) => {
    switch (status) {
      case "Em andamento":
        return "#22c55e";
      case "Finalizado":
        return "#6b7280";
      case "Planejado":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  }};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const ChallengeContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const ChallengeTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ChallengeSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ChallengeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const ChallengeMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChallengeDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ChallengeParticipants = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const ChallengeType = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.surfaceLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

interface FilterState {
  campanha: string;
  desafio: string;
  tipo: string;
  status: string;
}

interface AvatarWithFallbackProps {
  src: string;
  alt: string;
  position: number;
}

const AvatarWithFallback: React.FC<AvatarWithFallbackProps> = ({
  src,
  alt,
  position,
}) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <PodiumAvatarWrapper position={position}>
      {!imageError ? (
        <PodiumAvatar src={src} alt={alt} onError={handleImageError} />
      ) : (
        alt.charAt(0).toUpperCase()
      )}
    </PodiumAvatarWrapper>
  );
};

interface ChallengeImageProps {
  imageUrl: string;
  title: string;
  children: React.ReactNode;
}

const ChallengeImageWithFallback: React.FC<ChallengeImageProps> = ({
  imageUrl,
  title,
  children,
}) => {
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <ChallengeImageContainer>
      {!imageError ? (
        <ChallengeImageElement
          src={imageUrl}
          alt={title}
          onError={handleImageError}
        />
      ) : (
        <ChallengeImageFallback imageUrl="">{title}</ChallengeImageFallback>
      )}
      {children}
    </ChallengeImageContainer>
  );
};

const Gamificacao: React.FC = () => {
  const [filters, setFilters] = useState<FilterState>({
    campanha: "",
    desafio: "",
    tipo: "",
    status: "",
  });

  const handleFilterChange = (filterType: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      campanha: "",
      desafio: "",
      tipo: "",
      status: "",
    });
  };

  const filteredChallenges = useMemo(() => {
    return gamificationData.desafios.filter((challenge: any) => {
      // Filtro por campanha (busca no título)
      if (
        filters.campanha &&
        filters.campanha !== "Todas as campanhas" &&
        !challenge.titulo.toLowerCase().includes(filters.campanha.toLowerCase())
      ) {
        return false;
      }

      // Filtro por desafio específico (busca no título)
      if (
        filters.desafio &&
        filters.desafio !== "Todos os desafios" &&
        !challenge.titulo.toLowerCase().includes(filters.desafio.toLowerCase())
      ) {
        return false;
      }

      // Filtro por tipo
      if (
        filters.tipo &&
        filters.tipo !== "Todos os tipos" &&
        challenge.tipo !== filters.tipo
      ) {
        return false;
      }

      // Filtro por status/categoria
      if (
        filters.status &&
        filters.status !== "Todos os status" &&
        challenge.categoria !== filters.status
      ) {
        return false;
      }

      return true;
    });
  }, [filters]);

  return (
    <PageContainer>
      <PageTitle>Gamificação</PageTitle>

      {/* Rankings */}
      <TopSection>
        <RankingCard>
          <RankingTitle>{gamificationData.rankingGeral.titulo}</RankingTitle>
          <RankingSubtitle>
            {gamificationData.rankingGeral.subtitulo}
          </RankingSubtitle>
          <RankingContainer>
            <TopRanking>
              {gamificationData.rankingGeral.usuarios
                .filter((user: any) => user.posicao <= 3)
                .map((user: any) => (
                  <RankingPodium key={user.id} position={user.posicao}>
                    <TrophyIcon position={user.posicao}>
                      <Trophy size={user.posicao === 1 ? 28 : 20} />
                    </TrophyIcon>
                    <AvatarWithFallback
                      position={user.posicao}
                      src={user.avatar}
                      alt={user.nome}
                    />
                    <PodiumName position={user.posicao}>{user.nome}</PodiumName>
                    <PodiumScore position={user.posicao}>
                      {user.pontos.toLocaleString()}
                    </PodiumScore>
                  </RankingPodium>
                ))}
            </TopRanking>
          </RankingContainer>
        </RankingCard>

        <RankingCard>
          <RankingTitle>
            {gamificationData.rankingDepartamento.titulo}
          </RankingTitle>
          <RankingSubtitle>
            {gamificationData.rankingDepartamento.subtitulo}
          </RankingSubtitle>
          <RankingList>
            {gamificationData.rankingDepartamento.departamentos.map(
              (dept: any, index: number) => (
                <RankingItem key={dept.id}>
                  <Avatar src={dept.avatar} alt={dept.nome} />
                  <UserInfo>
                    <UserName>{dept.nome}</UserName>
                    <UserDepartment>{dept.departamento}</UserDepartment>
                  </UserInfo>
                  <Points>{dept.pontos} PONTOS</Points>
                </RankingItem>
              )
            )}
          </RankingList>
        </RankingCard>
      </TopSection>

      {/* Stats Cards */}
      <StatsSection>
        <SimpleStatsCard>
          <SimpleStatsTitle>Engajamento da campanha</SimpleStatsTitle>
          <SimpleStatsContent>
            <SimpleStatsValue>
              {gamificationData.engajamento.percentual}%
            </SimpleStatsValue>
            <SimpleStatsSubtitle
              positive={gamificationData.engajamento.variacao > 0}
            >
              {gamificationData.engajamento.variacao > 0 ? "↑" : "↓"}{" "}
              {Math.abs(gamificationData.engajamento.variacao)}%{" "}
              {gamificationData.engajamento.descricao}
            </SimpleStatsSubtitle>
          </SimpleStatsContent>
        </SimpleStatsCard>

        <ChartCard>
          <ChartTitle>{gamificationData.origemPontos.titulo}</ChartTitle>
          <ChartContentContainer>
            <ChartSection>
              <ResponsivePie
                data={gamificationData.origemPontos.dados.map((item: any) => ({
                  id: item.id,
                  label: item.nome,
                  value: item.valor,
                  color: item.cor,
                }))}
                margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                innerRadius={0.4}
                padAngle={2}
                cornerRadius={4}
                activeOuterRadiusOffset={8}
                colors={{ datum: "data.color" }}
                borderWidth={1}
                borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                enableArcLinkLabels={false}
                enableArcLabels={false}
                legends={[]}
              />
            </ChartSection>
            <LegendSection>
              {gamificationData.origemPontos.dados.map((item: any) => (
                <LegendItem key={item.id}>
                  <LegendColor color={item.cor} />
                  <LegendText>
                    <LegendPercentage>{item.valor}%</LegendPercentage>
                    {item.nome}
                  </LegendText>
                </LegendItem>
              ))}
            </LegendSection>
          </ChartContentContainer>
        </ChartCard>

        <SimpleStatsCard>
          <SimpleStatsTitle>Participantes</SimpleStatsTitle>
          <SimpleStatsContent>
            <SimpleStatsValue>
              {gamificationData.participantes.total.toLocaleString()}
            </SimpleStatsValue>
            <SimpleStatsSubtitle
              positive={gamificationData.participantes.variacao > 0}
            >
              {gamificationData.participantes.variacao > 0 ? "↑" : "↓"}{" "}
              {Math.abs(gamificationData.participantes.variacao)}%{" "}
              {gamificationData.participantes.descricao}
            </SimpleStatsSubtitle>
          </SimpleStatsContent>
        </SimpleStatsCard>
      </StatsSection>

      {/* Challenges Section */}
      <ChallengesSection>
        <SectionTitle>Desafios</SectionTitle>

        <FilterSection>
          <FilterSelect
            value={filters.campanha}
            onChange={(e) => handleFilterChange("campanha", e.target.value)}
          >
            <option value="">Campanha</option>
            {gamificationData.filtros.campanhas.map((campanha: string) => (
              <option key={campanha} value={campanha}>
                {campanha}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filters.desafio}
            onChange={(e) => handleFilterChange("desafio", e.target.value)}
          >
            <option value="">Desafio</option>
            {gamificationData.filtros.desafios.map((desafio: string) => (
              <option key={desafio} value={desafio}>
                {desafio}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filters.tipo}
            onChange={(e) => handleFilterChange("tipo", e.target.value)}
          >
            <option value="">Tipo</option>
            {gamificationData.filtros.tipos.map((tipo: string) => (
              <option key={tipo} value={tipo}>
                {tipo}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">Status</option>
            {gamificationData.filtros.status.map((status: string) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </FilterSelect>

          <FilterButton>Buscar</FilterButton>

          <FilterButton onClick={clearFilters}>
            <Filter size={16} />
            Limpar filtros
          </FilterButton>
        </FilterSection>

        <ChallengesGrid>
          {filteredChallenges.map((challenge: any) => (
            <ChallengeCard
              key={challenge.id}
              totalCards={filteredChallenges.length}
            >
              <ChallengeImageWithFallback
                imageUrl={challenge.imagem}
                title={challenge.titulo}
              >
                <ChallengeStatus status={challenge.categoria}>
                  {challenge.categoria}
                </ChallengeStatus>
              </ChallengeImageWithFallback>
              <ChallengeContent>
                <ChallengeType>{challenge.tipo}</ChallengeType>
                <ChallengeTitle>{challenge.titulo}</ChallengeTitle>
                <ChallengeSubtitle>{challenge.descricao}</ChallengeSubtitle>
                <ChallengeInfo>
                  <ChallengeMetaRow>
                    <ChallengeParticipants>
                      <Users size={14} />
                      {challenge.participantes}
                    </ChallengeParticipants>
                    <ChallengeDate>
                      <Calendar size={14} />
                      {challenge.dataInicio} - {challenge.dataFim}
                    </ChallengeDate>
                  </ChallengeMetaRow>
                </ChallengeInfo>
              </ChallengeContent>
            </ChallengeCard>
          ))}
        </ChallengesGrid>
      </ChallengesSection>
    </PageContainer>
  );
};

export default Gamificacao;
